import { useState } from "react";

interface RejectionDialogProps {
  readonly isOpen: boolean;
  readonly loading: boolean;
  readonly submissionTitle: string;
  readonly onConfirm: (reason: string, notes: string) => void;
  readonly onCancel: () => void;
}

const REJECTION_REASONS = [
  { value: "", label: "Select a reason..." },
  { value: "incomplete_metadata", label: "Incomplete Metadata" },
  { value: "quality_issues", label: "Quality Issues" },
  { value: "rights_licensing", label: "Rights/Licensing Issues" },
  { value: "content_violations", label: "Content Violations" },
  { value: "other", label: "Other" },
] as const;

function DialogHeader({ submissionTitle }: { readonly submissionTitle: string }) {
  return (
    <div className="flex items-start gap-3 mb-4">
      <div className="w-10 h-10 bg-red-600/20 rounded-full flex items-center justify-center flex-shrink-0">
        <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <div className="flex-1">
        <h3 className="text-xl font-bold text-white mb-1">Reject Submission</h3>
        <p className="text-sm text-neutral-400">{submissionTitle}</p>
      </div>
    </div>
  );
}

/**
 * Dialog component for rejecting content submissions.
 * Requires a rejection reason and allows additional notes.
 */
export function RejectionDialog({ isOpen, loading, submissionTitle, onConfirm, onCancel }: RejectionDialogProps) {
  const [reason, setReason] = useState<string>("");
  const [notes, setNotes] = useState("");

  const handleConfirm = () => {
    if (!reason) return; // Should not happen due to button disabled state
    onConfirm(reason, notes);
    // Reset state after confirmation
    setReason("");
    setNotes("");
  };

  const handleCancel = () => {
    onCancel();
    // Reset state on cancel
    setReason("");
    setNotes("");
  };

  const isReasonSelected = reason !== "";

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-black/60 cursor-default"
        onClick={handleCancel}
        aria-label="Close dialog"
        tabIndex={-1}
      />

      {/* Dialog */}
      <div className="relative bg-neutral-900 rounded-lg shadow-xl w-full max-w-md p-6 mx-4">
        <DialogHeader submissionTitle={submissionTitle} />

        <div className="space-y-6">
          {/* Notification Banner */}
          <div className="rounded-lg bg-red-600/10 border border-red-600/20 p-3 text-sm text-red-400">
            The content provider will be notified of this rejection with the reason and notes you provide.
          </div>

          {/* Required Rejection Reason */}
          <div className="space-y-2">
            <label htmlFor="reason" className="text-sm font-medium text-white">
              Rejection Reason <span className="text-red-500">*</span>
            </label>
            <select
              id="reason"
              value={reason}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setReason(e.target.value)}
              disabled={loading}
              className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
            >
              {REJECTION_REASONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          {/* Optional Notes */}
          <div className="space-y-2">
            <label htmlFor="notes" className="text-sm font-medium text-white">
              Additional Notes (Optional)
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
              placeholder="Add specific feedback or guidance for the provider..."
              rows={4}
              disabled={loading}
              className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white placeholder-neutral-500 resize-none focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
            />
            <p className="text-xs text-neutral-500">
              Provide additional context to help the provider understand the rejection.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={handleCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-neutral-300 bg-neutral-800 rounded-lg hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isReasonSelected || loading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Rejecting..." : "Reject Submission"}
          </button>
        </div>
      </div>
    </div>
  );
}
